import { createEntityAdapter, createSlice } from "@reduxjs/toolkit";
import { listRuleGeneratedContent } from "store/ruleConfiguration/actions";

import { getClipListDetails, getClipLists, getInfinitePaginatedClipLists, getStreamClipLists } from "./actions";

export const clipListEntityAdapter = createEntityAdapter({
  selectId: (entity) => entity._id,
});

const initialState = clipListEntityAdapter.getInitialState({
  count: 0,
  modals: {},
  groupByDate: {},
  searchResultIds: [],
});

export const clipListSlice = createSlice({
  name: "clipList",
  initialState: initialState,
  reducers: {
    showClipListCreateModal: (state, action) => {
      state.modals.clipListCreateModal = action.payload;
    },
    showAddClipToClipListModal: (state, action) => {
      state.modals.addClipToClipListModal = action.payload;
    },
    showClipListDeleteModal: (state, action) => {
      state.modals.clipListDeleteModal = action.payload;
    },
    showClipListAllFiltersModal: (state, action) => {
      state.modals.clipListAllFiltersModal = action.payload;
    },
    showAssignRatingModal: (state, action) => {
      state.modals.assignRatingModal = action.payload;
    },
    showClipListRegenerateHighlightModal: (state, action) => {
      state.modals.clipListRegenerateHighlightModal = action.payload;
    },
    showRenameClipListModal: (state, action) => {
      state.modals.renameClipListModal = action.payload;
    },
    showEditingSidebarContentUploadModal: (state, action) => {
      state.modals.editingSidebarContentUploadModal = action.payload;
    },
    showClipListTemplateApplyModal: (state, action) => {
      state.modals.clipListTemplateApplyModal = action.payload;
    },
    showClipListSelectVariantModal: (state, action) => {
      state.modals.clipListSelectVariantModal = action.payload;
    },

    updateClipListValues: (state, action) => {
      const { clipListId, ...changes } = action.payload;

      clipListEntityAdapter.updateOne(state, {
        id: clipListId,
        changes: {
          ...changes,
        },
      });
    },

    updateProgressPercent: (state, action) => {
      const { progressPercent, clipListId, status } = action.payload;
      const existing = state.entities[clipListId];
      const changes = { progressPercent };

      if (status) {
        changes.previewData = {
          ...(existing?.previewData ?? {}),
          status,
        };
      }

      clipListEntityAdapter.updateOne(state, {
        id: clipListId,
        changes,
      });
    },
    updateClipListClips: (state, action) => {
      const { clipListId, clips } = action.payload;
      if (!state.entities[clipListId]) {
        return;
      }

      clipListEntityAdapter.updateOne(state, {
        id: clipListId,
        changes: { clips },
      });
    },
    updateClipListEntity: (state, action) => {
      const clipListData = action.payload;
      if (clipListData && clipListData?._id) {
        const clips = clipListData?.clips?.map((clip) => clip?._id);

        state.entities[clipListData?._id] = {
          ...state.entities[clipListData?._id],
          ...clipListData,
          clips,
        };
      }
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getClipLists.fulfilled, (state, action) => {
      if (action.payload.status) {
        if (action.payload.entities?.clipList) {
          clipListEntityAdapter.setAll(state, action.payload.entities.clipList);
          state.count = action.payload.totalCount;
        } else {
          state.ids = [];
          state.count = 0;
        }
      }
    });
    builder.addCase(getStreamClipLists.fulfilled, (state, action) => {
      if (action.payload.success) {
        if (action?.meta?.arg?.isSearch) {
          state.searchResultIds = action?.payload?.entities?.clipList
            ? Object.values(action.payload.entities.clipList)
                .filter((clipList) => clipList && clipList._id)
                .map((clipList) => clipList._id)
            : [];
        }
        if (action.payload.entities?.clipList) {
          if (action.meta.arg.upsert) {
            clipListEntityAdapter.upsertMany(state, action.payload.entities.clipList);
          } else {
            clipListEntityAdapter.setAll(state, action.payload.entities.clipList);
          }
          if (!action.meta.arg.isSearch) {
            // don't update these counts if its a search request
            state.count = action.payload.highlightCount;
            state.generatedHighlightCount = action.payload.generatedCount;
            state.draftHighlightCount = action.payload.draftCount;
            state.fromTheRulesHighlightCount = action.payload.fromTheRulesCount;
          }
        } else if (!action.meta.arg.upsert) {
          state.ids = [];
          state.entities = {};
          state.count = 0;
          state.generatedHighlightCount = action.payload.generatedCount || 0;
          state.draftHighlightCount = action.payload.draftCount || 0;
          state.fromTheRulesHighlightCount = action.payload.fromTheRulesCount || 0;
        }
      }
    });
    builder.addCase(getClipListDetails.fulfilled, (state, action) => {
      if (action?.payload.success) {
        Object.keys(action.payload.entities.clipList).forEach((clipListId) => {
          state.entities[clipListId] = action.payload.entities.clipList[clipListId];
        });
      }
    });

    builder.addCase(getInfinitePaginatedClipLists.fulfilled, (state, action) => {
      if (action.payload.status) {
        if (action.payload.entities?.clipList) {
          /**
           * If its a new req with updated filters, them don't append in prev state
           */
          const isNewReq = action?.meta?.arg?.payload?.params?.page_no === 1;

          if (isNewReq) {
            clipListEntityAdapter.setAll(state, action.payload.entities?.clipList);
            state.groupByDate = action.payload.entities?.groupByDateClipList || {};
          } else {
            clipListEntityAdapter.addMany(state, action.payload.entities?.clipList); // infinite scroll pagination
            state.groupByDate = {
              ...state.groupByDate,
              ...(action.payload.entities?.groupByDateClipList || {}),
            };
          }

          state.count = action.payload.totalCount;
        } else {
          state.ids = [];
          state.groupByDate = {};
          state.count = 0;
        }
      }
    });
    builder.addCase(listRuleGeneratedContent.fulfilled, (state, action) => {
      if (action.payload.entities?.clipList) {
        clipListEntityAdapter.setAll(state, action.payload.entities.clipList);
        state.count = action.payload.data.totalCount;
      } else {
        state.ids = [];
        state.count = 0;
      }
    });
  },
});

export const {
  showClipListCreateModal,
  showAddClipToClipListModal,
  showClipListDeleteModal,
  showClipListAllFiltersModal,
  showAssignRatingModal,
  updateClipListValues,
  showClipListRegenerateHighlightModal,
  updateProgressPercent,
  showRenameClipListModal,
  showEditingSidebarContentUploadModal,
  updateClipListClips,
  showClipListTemplateApplyModal,
  updateClipListEntity,
  showClipListSelectVariantModal,
} = clipListSlice.actions;

export default clipListSlice.reducer;
